/**
 * RunLogsTab — Debug log timeline from debuggerPayload.timeline.
 * Density follows detail level:
 *   summary → only 'started' / 'ok' / 'failed' events
 *   debug   → all events
 *   full    → all events + audit rows appended
 */
import React from 'react';
import { List } from 'lucide-react';
import { type DebugPayload, type DetailLevel, formatTime } from './run-detail-types.js';

const SUMMARY_TYPES = new Set(['started', 'ok', 'completed', 'failed', 'error']);

interface RunLogsTabProps {
  payload: DebugPayload;
  detail: DetailLevel;
}

export const RunLogsTab: React.FC<RunLogsTabProps> = ({ payload, detail }) => {
  const { timeline, audit } = payload;

  const filteredTimeline =
    detail === 'summary'
      ? timeline.filter((item) => SUMMARY_TYPES.has(item.type) || SUMMARY_TYPES.has(item.severity))
      : timeline;

  const showAudit = detail === 'full' && audit.length > 0;

  const severityClass = (sev: string): string => {
    if (sev === 'error') return 'text-danger';
    if (sev === 'warning') return 'text-warning';
    return 'text-body';
  };

  return (
    <div className="bg-surface border border-border rounded-xl overflow-hidden mb-4" style={{ margin: 0 }}>
      {/* Card header */}
      <div className="flex items-center justify-between gap-2.5 px-4 py-3 border-b border-border bg-surface-alt">
        <span className="font-mono text-[10.5px] uppercase tracking-[1.2px] text-muted flex items-center gap-2">
          <List size={14} className="text-faint" />
          Debug log
        </span>
        <span className="font-mono text-[10.5px] text-faint">detail level: {detail}</span>
      </div>

      <div className="p-4">
        {filteredTimeline.length === 0 && !showAudit ? (
          <p className="text-[12.5px] text-muted">No events recorded at this detail level.</p>
        ) : (
          <div className="font-mono text-[11.5px] bg-bg rounded-lg px-3 py-3 space-y-[3px] max-h-[420px] overflow-auto">
            {filteredTimeline.map((item, idx) => (
              <div
                key={`${item.type}-${idx}`}
                className="grid gap-3 items-start"
                style={{ gridTemplateColumns: '70px minmax(0,1fr)' }}
              >
                <span className="text-faint shrink-0">{formatTime(item.at)}</span>
                <span>
                  {item.stepId && <span className="text-accent-text mr-2">{item.stepId}</span>}
                  <span className={severityClass(item.severity)}>{item.label}</span>
                </span>
              </div>
            ))}

            {/* full detail: append audit events */}
            {showAudit && (
              <>
                <div className="border-t border-border my-2" />
                <div className="text-faint text-[10px] uppercase tracking-wider mb-1">Audit events</div>
                {audit.map((evt) => (
                  <div
                    key={evt.id}
                    className="grid gap-3 items-start"
                    style={{ gridTemplateColumns: '70px minmax(0,1fr)' }}
                  >
                    <span className="text-faint shrink-0">{formatTime(evt.createdAt)}</span>
                    <span>
                      <span className="text-muted mr-2">[audit]</span>
                      <span className={severityClass(evt.severity)}>{evt.eventType}</span>
                      {evt.stepId && <span className="text-faint ml-2">· step {evt.stepId}</span>}
                    </span>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
